"use client";

import { useMemo, useState } from "react";
import { CheckCircle2, Play } from "lucide-react";

import { cn } from "@/lib/utils";
import { VOICE_WAVEFORM } from "@/lib/voice-waveform";
import { MARKETING_SCENARIOS } from "@/lib/assistant-scenarios";

export function ActionsStepperSection() {
  const [active, setActive] = useState(MARKETING_SCENARIOS[0]?.id ?? "book");

  const scenario = useMemo(() => {
    return MARKETING_SCENARIOS.find((s) => s.id === active) ?? MARKETING_SCENARIOS[0];
  }, [active]);

  const isVoice = scenario.messageType === "voice";

  return (
    <section id="actions" data-testid="section-actions" className="py-16 sm:py-24">
      <div className="mx-auto max-w-2xl text-center">
        <p className="eyebrow">Actions, not answers</p>
        <h2 className="mt-4 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">
          Watch your assistant handle the <span className="ink-underline">details</span>.
        </h2>
        <p className="mt-4 text-lg font-medium leading-relaxed text-foreground/70">
          You tap or talk. Dobbix takes care of the details and checks back when it’s done.
        </p>
      </div>

      {/* Scenario selector */}
      <div className="mt-10 flex justify-center">
        <div className="segmented-shell inline-flex gap-1 rounded-full p-1">
          {MARKETING_SCENARIOS.map((s) => {
            const isActive = s.id === active;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActive(s.id)}
                aria-pressed={isActive}
                className={cn(
                  "segmented-item rounded-full px-4 py-2 text-sm font-semibold",
                  isActive ? "segmented-item-active text-foreground" : "text-muted-foreground",
                )}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto mt-10 grid max-w-5xl gap-5 lg:grid-cols-2">
        {/* Message */}
        <div className="paper-card card-hover-lift rounded-3xl p-7">
          <div className="flex items-center justify-between gap-3">
            <span className="eyebrow text-foreground/60">Your message</span>
            <span className="stamp rounded-full px-3 py-1 text-[11px] font-semibold text-foreground/60">
              {isVoice ? "Voice note" : "Text"}
            </span>
          </div>

          <div className="mt-6 rounded-3xl border border-border/70 bg-background/60 px-5 py-5">
            {isVoice ? (
              <>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="paper-tab flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl">
                      <Play className="h-4 w-4 text-foreground/80" aria-hidden="true" />
                    </span>
                    <div className="flex items-end gap-0.5" aria-hidden="true">
                      {VOICE_WAVEFORM.map((height, idx) => (
                        <span
                          key={idx}
                          className="waveform-bar w-1 rounded-full bg-primary/55"
                          style={{
                            height: `${height}px`,
                            animationDelay: `${idx * 70}ms`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <span className="shrink-0 text-xs font-medium text-muted-foreground tabular-nums">
                    {scenario.voiceDuration ?? "0:00"}
                  </span>
                </div>

                <div className="mt-4 rounded-2xl border border-border/60 bg-background/60 px-4 py-3">
                  <p className="text-xs font-semibold text-foreground/70">Transcript</p>
                  <p className="mt-1 text-sm font-medium text-foreground">{scenario.message}</p>
                </div>
              </>
            ) : (
              <p className="text-base font-medium text-foreground">{scenario.message}</p>
            )}

            <p className="mt-3 text-sm text-muted-foreground">
              No new habits. Just {isVoice ? "send a voice note" : "text"} like you normally do.
            </p>
          </div>

          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" />
            Dobbix confirms what happened, then quietly follows up.
          </div>
        </div>

        {/* Steps */}
        <div className="paper-card card-hover-lift rounded-3xl p-7">
          <div className="flex items-center justify-between gap-3">
            <span className="eyebrow text-foreground/60">What Dobbix does</span>
            <span className="stamp rounded-full px-3 py-1 text-[11px] font-semibold text-foreground/60">
              Behind the scenes
            </span>
          </div>

          <ol className="mt-6 space-y-3">
            {scenario.steps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <li
                  key={step.title}
                  className="flex gap-4 rounded-2xl border border-border/70 bg-background/55 px-4 py-4"
                >
                  <div className="paper-tab flex h-10 w-10 items-center justify-center rounded-2xl">
                    <Icon className="h-5 w-5 text-foreground/80" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      <span className="text-[11px] text-foreground/55 tabular-nums">
                        {String(idx + 1).padStart(2, "0")}
                      </span>{" "}
                      {step.title}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      {step.body}
                    </p>
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="mt-6 rounded-2xl border border-border/70 bg-primary/[0.06] px-4 py-4">
            <p className="eyebrow text-foreground/60">Result</p>
            <p className="mt-2 text-sm font-medium text-foreground">{scenario.result}</p>
          </div>
        </div>
      </div>
    </section>
  );
}
